/**
 * PersonalPageLink — THE surface for "הקישור לעמוד האישי של הלקוח/ה".
 *
 * One component, three homes: the create-client finish step, the patient
 * profile (under the status chip) and the workspace header. The mental model
 * it enforces: the client is handed a link to THEIR OWN PAGE — food logging
 * and measurements work there even before any menu exists — never "a link to
 * a menu".
 *
 * The link is one durable link per client. `createMenuLink` returns the
 * existing one when there is one (`reused`), so fetching it on mount is safe
 * and idempotent; a client that somehow has none gets one minted here.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { LinkSimple } from "@phosphor-icons/react";
import { ShareLinkActions } from "../../shared";
import { createMenuLink, sendMenuLinkSms } from "../../../firebase";
import { useToast } from "../../../contexts/ToastContext";
import "./PersonalPageLink.css";
import LoaderLoop from "../../shared/loader/LoaderLoop";

export const PERSONAL_PAGE_LINK_TITLE = "הקישור לעמוד האישי של הלקוח/ה";

const EXPLANATIONS = {
  // What happens next, per surface — one short sentence each.
  card: "שלחו את הקישור ללקוח/ה: בעמוד הזה הוא/היא רואה/ה את התפריט הפעיל, מדווח/ת מה אכל/ה ומוסיף/ה מדידות. הכניסה מאומתת ב-4 הספרות האחרונות של הטלפון.",
  row: "קישור קבוע אחד לעמוד של הלקוח/ה — תפריט, דיווח אכילה ומדידות. עובד גם לפני שנבנה תפריט.",
  compact: "",
};

/**
 * @param {object} props
 * @param {string} props.clinicId
 * @param {object} props.patient      needs id + phone
 * @param {"card"|"row"|"compact"} [props.variant]  card = finish step,
 *        row = profile / workspace header, compact = inside an empty state
 * @param {string} [props.explanation] override the default sentence
 */
function PersonalPageLink({ clinicId, patient, variant = "row", explanation }) {
  const { showSuccess, showError } = useToast();
  const [link, setLink] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const mountedRef = useRef(true);

  const patientId = patient?.id;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchLink = useCallback(async () => {
    if (!clinicId || !patientId) return;
    setIsLoading(true);
    setFailed(false);
    try {
      const result = await createMenuLink(clinicId, patientId);
      if (mountedRef.current) setLink(result);
    } catch (error) {
      console.error("[CK-2624] Error preparing the client's personal page link:", {
        errorCode: "CK-2624",
        errorMessage: error?.message,
        clinicId,
        patientId,
        timestamp: new Date().toISOString(),
      });
      if (mountedRef.current) setFailed(true);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [clinicId, patientId]);

  // The link exists from creation onward — fetch it on mount, don't wait for a click
  useEffect(() => {
    setLink(null);
    fetchLink();
  }, [fetchLink]);

  const handleSendSms = async () => {
    if (!link) return;
    try {
      await sendMenuLinkSms(clinicId, patientId, link.linkId);
      showSuccess("הקישור נשלח ב-SMS");
    } catch (error) {
      console.error("[CK-2622] Error sending the personal page link by SMS:", {
        errorCode: "CK-2622",
        errorMessage: error?.message,
        clinicId,
        patientId,
        timestamp: new Date().toISOString(),
      });
      showError(
        error?.message?.includes("SMS_QUOTA_EXCEEDED")
          ? "מכסת ה-SMS החודשית נוצלה במלואה"
          : "שליחת ה-SMS נכשלה"
      );
    }
  };

  const text = explanation ?? EXPLANATIONS[variant];

  return (
    <div className={`ppl ppl-${variant}`}>
      <div className="ppl-text">
        <span className="ppl-title">
          <LinkSimple size={14} weight="bold" />
          {PERSONAL_PAGE_LINK_TITLE}
        </span>
        {text && <span className="ppl-explain">{text}</span>}
      </div>

      {variant === "card" && link && (
        <code className="ppl-url" dir="ltr">
          {link.url}
        </code>
      )}

      {link ? (
        <ShareLinkActions
          url={link.url}
          phone={patient?.phone}
          onSendSms={handleSendSms}
          message="שלום, העמוד האישי שלך מוכן"
          size={variant === "card" ? "md" : "sm"}
        />
      ) : isLoading ? (
        <span className="ppl-pending">
          <LoaderLoop size="xs" />
          מכין את הקישור...
        </span>
      ) : failed ? (
        <button type="button" className="ppl-retry-btn" onClick={fetchLink}>
          <LinkSimple size={15} weight="bold" />
          הקישור לא נטען — נסו שוב
        </button>
      ) : null}
    </div>
  );
}

export default PersonalPageLink;
